import VolumeDown from '@mui/icons-material/VolumeDown';
import VolumeMuteIcon from '@mui/icons-material/VolumeMute';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Slider, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import moment from 'moment';
import React, { Fragment, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useTranslation } from 'react-i18next';
import PauseIcon from '../icons/PauseIcon';
import PlayIcon from '../icons/PlayIcon';

const SoundDialog = forwardRef(({ message, startDate, endDate, noEnd, noMinutes }, ref) => {
    const theme = useTheme();
    const belowsm = useMediaQuery(theme.breakpoints.down("sm"));

    const [open, setOpen] = useState(false);
    const [iconColor, setIconColor] = useState(theme.palette.text.primary);
    const [playing, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.5);
    const [url, setUrl] = useState("");

    const { t } = useTranslation();

    //eslint-disable-next-line
    const [audioAsState, setAudioAsState] = useState(new Audio());

    useEffect(() => {
        let audio = new Audio(url);
        audio.load();
        setAudioAsState(audio);
        audio.addEventListener("loadedmetadata", () => {
            setDuration(Math.trunc(audio.duration));
        });
        //have audio play from the beginning
        audio.addEventListener('loadeddata', () => {
            audio.play();
            setPlaying(true);
        });
        audio.addEventListener("timeupdate", () => {
            // console.log(`current time is ${Math.trunc(audio.currentTime)} seconds`)
            setCurrentTime(Math.trunc(audio.currentTime));
        });

        audio.addEventListener('ended', () => { setPlaying(false) });
        return () => {
            audio.removeEventListener('ended', () => { setPlaying(false) });
        };
        //eslint-disable-next-line
    }, [url])

    useImperativeHandle(ref, () => ({
        open(url) {
            setOpen(true);
            setUrl(`${url}?timestamp=${Date.now()}`);
        }
    }));

    const handleSliderChange = (event, value) => {
        setCurrentTime(value);
        audioAsState.currentTime = value;
    };

    const handleVolumeChange = (event, value) => {
        setVolume(value);
        audioAsState.volume = value;
    };

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        const formattedMinutes = minutes.toString().padStart(2, "0");
        const formattedSeconds = seconds.toString().padStart(2, "0");
        return `${formattedMinutes}:${formattedSeconds}`;
    };

    const handleClose = () => {
        audioAsState.pause();
        setUrl("");
        setPlaying(false);
        setCurrentTime(0);
        setDuration(0);
        setVolume(0.5);
        setOpen(false);
    }

    const handleKeyDown = (e) => {
        if (e.keyCode === 32) {
            if (playing) {
                audioAsState.pause();
            } else {
                audioAsState.play();
            }
        };
        setPlaying(!playing);
    };

    return (
        <Dialog
            fullWidth
            maxWidth={"sm"}
            open={open}
            onKeyDown={handleKeyDown}
            onClose={handleClose}
            // hideBackdrop
            sx={{
                "& .MuiDialog-paper": {
                    height: 275,
                    backgroundImage: "none",
                    "& .MuiPaper-elevation": {
                        elevation: 2
                    }
                }
            }}
        >
            <DialogTitle sx={{ display: "flex", justifyContent: "center", px: 1 }}>
                <Box sx={{ borderBottom: 2, borderBottomColor: "divider", width: "100%" }}>
                    <Typography noWrap sx={{ fontSize: 18, fontWeight: "bold", ml: 1 }}>{message || t("no-title")}</Typography>
                    <Typography sx={{ color: "text.secondary", fontSize: 14, ml: 1, mb: .5 }}>{`${noMinutes ? `${moment(startDate).format("DD.MM")} 00:00` : moment(startDate).format("DD.MM HH:mm")} - ${noEnd ? t("no-enddate") : noMinutes ? `${moment(endDate).format("DD.MM")} 00:00` : moment(endDate).format("DD.MM HH:mm")}`}</Typography>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ py: 0, pl: belowsm ? 1 : 7.5 }}>
                {belowsm ?
                    <Fragment>
                        <Grid container>
                            <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
                                <Typography sx={{ mt: 5 }}>{`${formatTime(currentTime)}`}</Typography>
                            </Grid>
                            <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
                                <Grid item>
                                    <IconButton onClick={() => { playing ? audioAsState.pause() : audioAsState.play(); setPlaying(!playing) }} onMouseEnter={() => { setIconColor(theme.palette.primary.main) }} onMouseLeave={() => { setIconColor(theme.palette.text.primary) }} sx={{ border: 2, p: 1.5, mt: 1, borderColor: iconColor }}>
                                        {playing ? <PauseIcon color={iconColor} fontSize={28} /> : <PlayIcon color={iconColor} fontSize={28} />}
                                    </IconButton>
                                </Grid>
                            </Grid>
                            <Grid item xs={4} sx={{ display: "flex", justifyContent: "center" }}>
                                <Typography sx={{ mt: 5 }}>{`${formatTime(duration) ?? "00:00"}`}</Typography>
                            </Grid>
                        </Grid>
                        <Grid container sx={{ display: "fles", justifyContent: "center", mt: 1 }}>
                            <Grid item xs={10}>
                                <Slider value={Math.trunc(currentTime)} onChange={handleSliderChange} size="medium" step={1} min={0} max={Math.trunc(duration)} sx={{ color: "text.primary", minWidth: 200, width: "100%", "&:hover": { color: "primary.main" } }} />
                            </Grid>
                        </Grid>
                    </Fragment> :
                    <Fragment>
                        <Grid container sx={{ pl: 1 }}>
                            <Grid item>
                                <IconButton onClick={() => { playing ? audioAsState.pause() : audioAsState.play(); setPlaying(!playing) }} onMouseEnter={() => { setIconColor(theme.palette.primary.main) }} onMouseLeave={() => { setIconColor(theme.palette.text.primary) }} sx={{ m: 1, ml: 2 }}>
                                    {playing ? <PauseIcon color={iconColor} fontSize={26} /> : <PlayIcon color={iconColor} fontSize={26} />}
                                </IconButton>
                            </Grid>
                            <Grid item sx={{ pt: 1, pl: 1 }}>
                                <Typography sx={{ mt: 1.5, mr: 1 }}>{`${formatTime(currentTime)}`}</Typography>
                            </Grid>
                            <Grid item sm={5} xs={8} sx={{ pt: 2 }}>
                                <Slider value={Math.trunc(currentTime)} onChange={handleSliderChange} size="small" step={1} min={0} max={Math.trunc(duration)} sx={{ color: "text.primary", mx: 1, "&:hover": { color: "primary.main" } }} />
                            </Grid>
                            <Grid item sx={{ pt: 1, pl: 2 }}>
                                <Typography sx={{ mt: 1.5, ml: 1 }}>{`${formatTime(duration) ?? "00:00"}`}</Typography>
                            </Grid>
                        </Grid>
                        <Grid container sx={{ pl: 3.2 }}>
                            <IconButton onClick={() => { setVolume(volume !== 0 ? 0 : 0.5); volume !== 0 ? audioAsState.volume = 0 : audioAsState.volume = 0.5 }}>
                                {volume === 0 ? <VolumeMuteIcon sx={{ color: "text.primary", fontSize: 26, "&:hover": { color: "primary.main" } }} /> : belowsm && volume >= 0.6 ? <VolumeUpIcon sx={{ color: "text.primary", fontSize: 26, "&:hover": { color: "primary.main" } }} /> : <VolumeDown sx={{ color: "text.primary", fontSize: 26, "&:hover": { color: "primary.main" } }} />}
                            </IconButton>
                            <Slider value={volume} onChange={handleVolumeChange} step={.01} min={0} max={1} size="small" sx={{ color: "text.primary", width: "55%", m: 1, pl: 0.5, pt: 2, "&:hover": { color: "primary.main" } }} />
                            <IconButton onClick={() => { setVolume(1); audioAsState.volume = 1 }} sx={{ ml: 1 }}>
                                <VolumeUpIcon sx={{ color: "text.primary", fontSize: 26, "&:hover": { color: "primary.main" } }} />
                            </IconButton>
                        </Grid>
                    </Fragment>
                }
            </DialogContent>
            <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
                <Grid container sx={{ width: "100%", borderTop: 2, borderColor: "divider" }}>
                    <Box key="spacer" sx={{ flexGrow: 1 }} />
                    <Button
                        variant="contained"
                        onClick={handleClose}
                        sx={{
                            mt: 1,
                            mr: 1,
                            width: belowsm ? "40%" : "auto",
                            backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main",
                            textTransform: "none",
                            border: theme.palette.mode === "dark" ? 1 : 0,
                            borderColor: "primary.main",
                            borderRadius: 6,
                            "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "primary.dark" : "primary.main" },
                        }}
                    >
                        {t("close")}
                    </Button>
                </Grid>
            </DialogActions>
        </Dialog>
    )
});

export default SoundDialog;